import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'
import { useConversation } from '@/v2/hooks/messages/useConversations'
import { useSendMessage } from '@/v2/hooks/messages/useMessages'

const QUERY_KEY = ['UnconfirmedAppointments']
const EMPTY_ARRAY = []

// The drawer has no socket: a reply from the patient shows within this long.
const THREAD_REFRESH_MS = 10000

// Rails answers an error as a string, or on a 422 as { field: [messages] }.
// labels names the fields worth naming in the message.
export const readError = async (res, fallback, labels = {}) => {
  const { errors } = await res.json().catch(() => ({}))
  if (typeof errors === 'string') return errors
  if (!errors || typeof errors !== 'object') return fallback

  const messages = Object.entries(errors).flatMap(([field, list]) =>
    []
      .concat(list)
      .map(text => (labels[field] ? `${labels[field]}: ${text}` : text))
  )
  return messages.join('. ') || fallback
}

// docs/v2/private/rest/appointments_doc.md#list-unconfirmed-appointments
// Upcoming appointments with no confirmed_at, every type, soonest first.
export const useUnconfirmedAppointments = ({ from, to }) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [...QUERY_KEY, from, to],
    queryFn: async () => {
      const params = new URLSearchParams({ unconfirmed: 'true', from, to })
      const res = await fetch(`${baseURL}/v2/rest/appointments?${params}`, {
        headers,
      })
      if (!res.ok) {
        throw new Error("We couldn't load the unconfirmed appointments")
      }
      return res.json()
    },
    enabled: Boolean(from && to),
  })

  return { appointments: data ?? EMPTY_ARRAY, isLoading, isError, refetch }
}

// Confirming and undoing it go through the appointment update, the same
// confirmed_at the calendar sets.
export const useSetAppointmentConfirmed = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: setConfirmed, ...rest } = useMutation({
    mutationFn: async ({ id, confirmed }) => {
      const res = await fetch(`${baseURL}/v2/rest/appointments/${id}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({
          appointment: {
            confirmed_at: confirmed ? new Date().toISOString() : null,
          },
        }),
      })
      if (!res.ok) {
        throw new Error(
          await readError(res, 'Could not update the appointment')
        )
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { setConfirmed, ...rest }
}

// docs/v2/private/rest/appointments/cancellations_doc.md
// Answers { notice: { sms, email } }, what each channel did. A 404 or a 422
// on base means someone cancelled it first, so the list is reloaded either way.
export const useCancelAppointment = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: cancelAppointment, ...rest } = useMutation({
    mutationFn: async ({ id, reason, sendNotice }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/appointments/${id}/cancellation`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            cancellation: { reason, send_notice: sendNotice },
          }),
        }
      )
      if (res.status === 404) {
        throw new Error('This appointment was already cancelled')
      }
      if (!res.ok) {
        throw new Error(
          await readError(res, 'Could not cancel the appointment')
        )
      }
      return res.json()
    },
    onSettled: () => {
      queryClient.invalidateQueries(QUERY_KEY)
    },
  })

  return { cancelAppointment, ...rest }
}

// The patient's text thread, through the Messages endpoints.
export const useAppointmentThread = conversationId => {
  const { conversation, isLoading } = useConversation(conversationId, {
    refetchInterval: THREAD_REFRESH_MS,
  })
  const { sendMessage } = useSendMessage(conversationId)

  return {
    messages: conversation?.messages?.records ?? EMPTY_ARRAY,
    isLoading,
    sendMessage,
  }
}
